import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCase, getNext } from './cases';
import { Placeholder, Fill, Reveal } from './Placeholder';
import { Media } from './Media';
import AIPipeline from './diagrams/AIPipeline';
import RulesDoc from './diagrams/RulesDoc';
import FastArchitecture from './diagrams/FastArchitecture';
import InstantUpdate from './diagrams/InstantUpdate';
import './CaseStudy.css';

/* Real authored artifacts that drop into gallery slots in place of a Placeholder */
const DIAGRAMS = {
  'ai-pipeline': AIPipeline,
  'rules-doc': RulesDoc,
  'fast-architecture': FastArchitecture,
  'instant-update': InstantUpdate,
};

/* Render a string, converting {{...}} tokens into visible Fill markers
   and *emphasis* into italics. */
function withFills(text) {
  if (!text) return null;
  const parts = text.split(/(\{\{[^}]+\}\}|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    const fill = p.match(/^\{\{([^}]+)\}\}$/);
    if (fill) return <Fill key={i}>{fill[1]}</Fill>;
    const em = p.match(/^\*([^*]+)\*$/);
    if (em) return <em key={i}>{em[1]}</em>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function CaseStudy({ slug }) {
  const c = getCase(slug);
  const next = getNext(slug);
  if (!c) return null;

  return (
    <motion.article
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* 0 — Context strip */}
      <div className="ppc-strip">
        <div className="pp-container ppc-strip__inner">
          <Link to="/product" className="ppc-strip__back">
            <span className="ppc-strip__arrow" aria-hidden="true">←</span>
            Back to work
          </Link>
        </div>
      </div>

      {/* 1 — Hero */}
      <header className="ppc-hero">
        <div className={`pp-container ppc-hero__grid${c.hero ? '' : ' ppc-hero__grid--textonly'}`}>
          <Reveal className="ppc-hero__text">
            <span className="pp-eyebrow ppc-hero__kicker">{c.accentLine}</span>
            <h1 className="ppc-hero__title">{c.title}</h1>
            <p className="ppc-hero__positioning">{withFills(c.positioning)}</p>
          </Reveal>
          {c.hero && (
            <Reveal delay={0.1} className="ppc-hero__visual">
              <Media src={c.hero.src} poster={c.hero.poster} ratio={c.hero.ratio} fit={c.hero.fit || 'contain'} mobileSrc={c.hero.mobileSrc} sound={c.hero.sound} alt={c.title} />
            </Reveal>
          )}
        </div>
      </header>

      {/* 2 — TL;DR card */}
      <section className="pp-container">
        {(() => {
          const meta = [['Role', c.tldr.role], ['Timeline', c.tldr.timeline]];
          if (c.tldr.team) meta.push(['Team', c.tldr.team]);
          const prose = [['The problem', c.tldr.problem], ['The result', c.tldr.result]];
          return (
            <Reveal className={`ppc-tldr${meta.length === 2 ? ' ppc-tldr--2' : ''}`}>
              {[...meta.map(([k, v]) => [k, v, false]), ...prose.map(([k, v]) => [k, v, true])].map(([k, v, full]) => (
                <div key={k} className={`ppc-tldr__cell${full ? ' ppc-tldr__cell--full' : ''}`}>
                  <span className="ppc-tldr__k">{k}</span>
                  <span className="ppc-tldr__v">{withFills(v)}</span>
                </div>
              ))}
            </Reveal>
          );
        })()}
      </section>

      {/* 3 — Context & constraint */}
      {c.contextMedia ? (
        <section className="ppc-section pp-container ppc-context--split">
          <Reveal className="ppc-context__media">
            <Media src={c.contextMedia.src} poster={c.contextMedia.poster} ratio={c.contextMedia.ratio} fit={c.contextMedia.fit || 'cover'} mobileSrc={c.contextMedia.mobileSrc} alt={c.contextMedia.alt} />
          </Reveal>
          <Reveal className="ppc-context__text">
            <span className="pp-eyebrow">Context &amp; constraint</span>
            <p className="ppc-prose">{withFills(c.context)}</p>
          </Reveal>
        </section>
      ) : (
        <section className="ppc-section pp-container pp-container--narrow">
          <Reveal>
            <span className="pp-eyebrow">Context &amp; constraint</span>
            <p className="ppc-prose">{withFills(c.context)}</p>
          </Reveal>
        </section>
      )}

      {/* 4 — The work: decisions + the killed version */}
      <section className="ppc-section pp-container">
        <Reveal className="pp-section-head">
          <div>
            <span className="pp-eyebrow">The work</span>
            <h2 className="pp-section-title">Decisions, forks, and trade-offs.</h2>
          </div>
        </Reveal>

        <div className="ppc-decisions">
          {c.decisions.map((d, i) => (
            <Reveal key={d.title} delay={i * 0.05} className="ppc-decision">
              <span className="ppc-decision__num">{String(i + 1).padStart(2, '0')}</span>
              <div className="ppc-decision__body">
                <h3 className="ppc-decision__title">{withFills(d.title)}</h3>
                <dl className="ppc-decision__grid">
                  <div><dt>Fork</dt><dd>{withFills(d.fork)}</dd></div>
                  <div><dt>Choice</dt><dd>{withFills(d.choice)}</dd></div>
                  <div><dt>Trade-off</dt><dd>{withFills(d.tradeoff)}</dd></div>
                </dl>
              </div>
            </Reveal>
          ))}
        </div>

        {c.killed && (
          <Reveal className="ppc-killed">
            <span className="ppc-killed__label">{c.killed.label}</span>
            <p className="ppc-killed__body">{withFills(c.killed.body)}</p>
          </Reveal>
        )}
      </section>

      {/* 5 — Craft gallery */}
      {c.gallery && c.gallery.length > 0 && (
      <section className="ppc-section pp-container">
        {c.galleryHeader !== false && (
          <Reveal className="pp-section-head pp-section-head--gallery">
            <div>
              <span className="pp-eyebrow">Craft gallery</span>
              <h2 className="pp-section-title">{c.galleryTitle || 'See it in the product.'}</h2>
              {c.galleryLead && <p className="ppc-gallery__lead">{c.galleryLead}</p>}
            </div>
          </Reveal>
        )}

        {c.galleryLayout === 'mirror' ? (
          <Reveal className="ppc-mirror">
            <figure className="ppc-mirror__side">
              <Media src={c.gallery[0].src} poster={c.gallery[0].poster} ratio={c.gallery[0].ratio} fit={c.gallery[0].fit || 'natural'} alt={c.gallery[0].label} />
              <figcaption className="ppc-mirror__cap">
                <span className="ppc-mirror__cap-role">{c.gallery[0].label}</span>
                {c.gallery[0].sublabel && <span className="ppc-mirror__cap-detail">{c.gallery[0].sublabel}</span>}
              </figcaption>
            </figure>
            <div className="ppc-mirror__link" aria-hidden="true">
              <span className="ppc-mirror__arrow">→</span>
              <span className="ppc-mirror__link-label">mirrors, live</span>
            </div>
            <figure className="ppc-mirror__side ppc-mirror__side--phone">
              <Media src={c.gallery[1].src} poster={c.gallery[1].poster} ratio={c.gallery[1].ratio} fit={c.gallery[1].fit || 'natural'} alt={c.gallery[1].label} />
              <figcaption className="ppc-mirror__cap">
                <span className="ppc-mirror__cap-role">{c.gallery[1].label}</span>
                {c.gallery[1].sublabel && <span className="ppc-mirror__cap-detail">{c.gallery[1].sublabel}</span>}
              </figcaption>
            </figure>
          </Reveal>
        ) : (
        <div className="ppc-gallery">
          {c.gallery.map((g, i) => {
            const Diagram = g.render && DIAGRAMS[g.render];
            const [rw, rh] = (g.ratio || '1 / 1').split('/').map((n) => parseFloat(n));
            const isPhone = rw && rh && rw / rh < 0.65; // tall phone clip (≈9/16): show at phone size, not stretched
            const size = i === 0 && !isPhone ? 'wide' : 'std';
            return (
              <Reveal key={g.label} delay={(i % 2) * 0.05} className={`ppc-gallery__item ppc-gallery__item--${size}${isPhone ? ' ppc-gallery__item--phone' : ''}`}>
                {g.src
                  ? <Media src={g.src} poster={g.poster} ratio={g.ratio} fit={g.fit || 'cover'} alt={g.label} />
                  : Diagram
                    ? <Diagram />
                    : <Placeholder tag={g.tag} label={g.label} ratio={g.ratio} />}
              </Reveal>
            );
          })}
        </div>
        )}
      </section>
      )}

      {/* 6 — Outcome */}
      <section className="ppc-outcome">
        <div className="pp-container pp-container--narrow">
          <Reveal>
            <span className="pp-eyebrow ppc-outcome__eyebrow">Outcome — so what?</span>
            <p className="ppc-outcome__body">{withFills(c.outcome)}</p>
          </Reveal>
        </div>
      </section>

      {/* 7 — Reflection */}
      <section className="ppc-section pp-container pp-container--narrow">
        <Reveal className="ppc-reflection">
          <span className="pp-eyebrow">Reflection</span>
          <p className="ppc-reflection__body">{withFills(c.reflection)}</p>
        </Reveal>
      </section>

      {/* 8 — Next case */}
      <Link to={`/product/${next.slug}`} className="ppc-next">
        <div className="pp-container ppc-next__inner">
          <span className="ppc-next__label">Next case · {next.number}</span>
          <span className="ppc-next__title">{next.title}</span>
          <span className="ppc-next__arrow">→</span>
        </div>
      </Link>
    </motion.article>
  );
}

export default CaseStudy;
